import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Eye, Bell, Trash2, UserMinus, ArrowLeft, Newspaper, 
  Settings, Search, TrendingUp, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import axios from 'axios';
import { getPartyConfig } from '@/lib/theme';
import SEO from '@/components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WatchlistPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { hasFullAccess } = useSubscription();
  
  const [watchlist, setWatchlist] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('watchlist');

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/watchlist');
      return;
    }
    fetchWatchlist();
    fetchAlerts();
  }, [user, token, navigate]);

  const fetchWatchlist = async () => {
    try {
      const response = await axios.get(`${API}/watchlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(response.data.watchlist || []);
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/watchlist/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const removeFromWatchlist = async (politicianName) => {
    try {
      await axios.delete(`${API}/watchlist/${encodeURIComponent(politicianName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(prev => prev.filter(p => p.politician_name !== politicianName));
      toast.success('Removed from watchlist');
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading watchlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="watchlist-page">
      <SEO 
        title="My Watchlist | The Watchtower"
        description="Track your watched politicians and get alerts when they appear in the news."
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/politicians" 
            className="inline-flex items-center text-slate-400 hover:text-white mb-4 text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Politicians
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/20 rounded-xl">
              <Eye className="h-8 w-8 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black">My Watchlist</h1>
              <p className="text-slate-300">
                {watchlist.length} politician{watchlist.length !== 1 ? 's' : ''} being monitored
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl p-1 border border-slate-200">
          <button
            onClick={() => setActiveTab('watchlist')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'watchlist' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="watchlist-tab"
          >
            <Eye className="h-4 w-4" />
            Watching ({watchlist.length})
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'alerts' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="alerts-tab"
          >
            <Newspaper className="h-4 w-4" />
            News Alerts ({alerts.length})
          </button>
        </div>

        {/* Watchlist Tab */}
        {activeTab === 'watchlist' && (
          <div>
            {watchlist.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <Eye className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h2 className="text-xl font-bold mb-2">No politicians watched yet</h2>
                <p className="text-slate-500 mb-6">
                  Start watching politicians to get alerts when they appear in the news.
                </p>
                <Link to="/politicians">
                  <Button className="bg-emerald-500 hover:bg-emerald-600">
                    <Search className="h-4 w-4 mr-2" />
                    Find Politicians to Watch
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {watchlist.map((item, idx) => {
                  const partyConfig = getPartyConfig(item.party);
                  const slug = item.politician_name.toLowerCase().replace(/ /g, '-').replace(/'/g, '');
                  
                  return (
                    <div 
                      key={idx}
                      className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                      data-testid={`watchlist-item-${idx}`}
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: partyConfig.color }}
                      >
                        {item.politician_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/politician/${slug}`}
                          className="font-bold text-slate-900 hover:text-emerald-600"
                        >
                          {item.politician_name}
                        </Link>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-medium"
                            style={{ backgroundColor: partyConfig.color + '20', color: partyConfig.color }}
                          >
                            {partyConfig.label || item.party}
                          </span>
                          <span>{item.politician_type}</span>
                          {item.constituency && <span>• {item.constituency}</span>}
                          {item.council && <span>• {item.council}</span>}
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFromWatchlist(item.politician_name)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-watch-${idx}`}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Quick Actions */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <Link 
                to="/politicians"
                className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:shadow-md transition-shadow"
              >
                <Search className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                <span className="font-medium">Find More Politicians</span>
              </Link>
              <Link 
                to="/settings"
                className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:shadow-md transition-shadow"
              >
                <Settings className="h-6 w-6 mx-auto mb-2 text-slate-500" />
                <span className="font-medium">Alert Settings</span>
              </Link>
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div>
            {alerts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <Newspaper className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h2 className="text-xl font-bold mb-2">No recent news alerts</h2>
                <p className="text-slate-500 mb-2">
                  {watchlist.length === 0 
                    ? "Add politicians to your watchlist to see news about them here."
                    : "When your watched politicians appear in the news, you'll see them here."
                  }
                </p>
                {!hasFullAccess && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-xl inline-block">
                    <p className="text-sm text-amber-800">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      Upgrade to Premium for email & SMS alerts
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <Link
                    key={idx}
                    to={`/article/${alert.id}`}
                    className="block bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                    data-testid={`alert-item-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <TrendingUp className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 line-clamp-2 mb-1">
                          {alert.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="text-emerald-600 font-medium">
                            {alert.politician_name}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="text-slate-500">{alert.source}</span>
                          <span className="text-slate-400">•</span>
                          <span className="text-slate-400">
                            {new Date(alert.published_date || alert.created_at).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistPage;
